import dayjs from "dayjs";
import { type CalendarEvent, type EventGroup } from "./types";

export const anchorDate = (e: CalendarEvent): Date =>
    e.softDate && e.softDate.getTime() < e.date.getTime() ? e.softDate : e.date;

// Review fix (FIBU-22 final pass): a today-aware anchor for the widget's horizon/
// grouping views ONLY (withinHorizon/groupEvent/deriveSummary, all via useHomeCalendar).
// `anchorDate` above (min(softDate, date)) always prefers the earlier soft date for an
// annual return - sensible while it's still upcoming, but once it has PASSED, anchoring
// on it makes `withinHorizon` compute a negative day count and silently drop the event
// even though the real, still-valid hard (StB-extended) deadline is months away. Once
// the soft date is past AND the hard date is still future, switch the anchor to the
// hard date; otherwise fall back to the plain `anchorDate` (pre-soft behavior
// unchanged). `groupEventsByMonth`/`buildEventsByDate` (the full-year page) deliberately
// keep using the plain, today-independent `anchorDate` - they list every deadline in a
// selected year, and making that month placement drift with today's date would be
// confusing there.
export const effectiveAnchorDate = (e: CalendarEvent, today: Date): Date => {
    if (!e.softDate) {
        return e.date;
    }
    const t = dayjs(today).startOf("day");
    const softPast = dayjs(e.softDate).startOf("day").isBefore(t);
    const hardFuture = !dayjs(e.date).startOf("day").isBefore(t);
    if (softPast && hardFuture) {
        return e.date;
    }
    return anchorDate(e);
};

export const withinHorizon = (e: CalendarEvent, today: Date, horizonDays: number): boolean => {
    const days = dayjs(effectiveAnchorDate(e, today)).startOf("day").diff(dayjs(today).startOf("day"), "day");
    // Tax deadlines are upcoming reminders only (spec §4) - overdue (negative) events are excluded.
    return days >= 0 && days <= horizonDays;
};

export const groupEvent = (e: CalendarEvent, today: Date): EventGroup => {
    const days = dayjs(effectiveAnchorDate(e, today)).startOf("day").diff(dayjs(today).startOf("day"), "day");
    if (days <= 0) {
        return "heute";
    }
    if (days <= 7) {
        return "woche";
    }
    if (days <= 31) {
        return "monat";
    }
    return "spaeter";
};

const EMPTY_GROUPS = (): Record<EventGroup, CalendarEvent[]> => ({ heute: [], woche: [], monat: [], spaeter: [] });

export const groupEvents = (
    events: CalendarEvent[],
    today: Date,
    horizonDays: number,
): Record<EventGroup, CalendarEvent[]> => {
    const groups = EMPTY_GROUPS();
    for (const e of events.filter((x) => withinHorizon(x, today, horizonDays))) {
        groups[groupEvent(e, today)].push(e);
    }
    for (const key of Object.keys(groups) as EventGroup[]) {
        groups[key].sort((a, b) => effectiveAnchorDate(a, today).getTime() - effectiveAnchorDate(b, today).getTime());
    }
    return groups;
};

// SP4 (FIBU-22): the dedicated calendar page's Agenda tab groups every event
// in the SELECTED YEAR by calendar month - deliberately NOT a reuse of
// `groupEvents`'s heute/woche/monat/spaeter buckets, which are relative to
// TODAY and only make sense for the widget's "next 60 days" view: for a
// full-year page (where the selected year can be in the past or future
// relative to today), those buckets would silently collapse to mostly
// "spaeter" or be entirely empty. No horizon cap, no overdue exclusion -
// every event whose anchor date falls in `year` is included (design spec
// §IV.7 Part B: "flat list of every event in the selected year").
export interface MonthSection {
    year: number;
    month: number; // 0-indexed
    events: CalendarEvent[];
}

// Review fix (FIBU-22 final pass): derived events of assessment year `year` can be
// DATED past Dec 31 of `year` - ustva-{year}-12/Q4 due 10.01.(year+1); annual-* soft
// 31.07.(year+1), hard end-Feb.(year+2); offenlegung-{year} due 31.12.(year+1) - and
// were previously dropped by the `d.getFullYear() !== year` filter below. `year + 2`
// (through February) is a hard, deterministic upper bound covering every one of those
// categories, so no unbounded future scan is needed. Events dated BEFORE `year` stay
// excluded, unchanged.
const SPILLOVER_END_YEAR_OFFSET = 2;

export const groupEventsByMonth = (events: CalendarEvent[], year: number): MonthSection[] => {
    const sections: MonthSection[] = Array.from({ length: 12 }, (_, month) => ({
        year,
        month,
        events: [] as CalendarEvent[],
    }));
    const overflowByKey = new Map<string, MonthSection>();

    for (const e of events) {
        const d = anchorDate(e);
        const eventYear = d.getFullYear();
        if (eventYear < year || eventYear > year + SPILLOVER_END_YEAR_OFFSET) {
            continue;
        }
        if (eventYear === year) {
            sections[d.getMonth()].events.push(e);
            continue;
        }
        const key = `${eventYear}-${d.getMonth()}`;
        let section = overflowByKey.get(key);
        if (!section) {
            section = { year: eventYear, month: d.getMonth(), events: [] };
            overflowByKey.set(key, section);
        }
        section.events.push(e);
    }

    const overflow = [...overflowByKey.values()].sort((a, b) => a.year - b.year || a.month - b.month);
    const result = [...sections, ...overflow];
    for (const section of result) {
        section.events.sort((a, b) => anchorDate(a).getTime() - anchorDate(b).getTime());
    }
    return result;
};

export interface CalendarSummary {
    kind: "today" | "next" | "empty";
    count?: number;
    nextEvent?: CalendarEvent;
    daysUntil?: number;
}

export const deriveSummary = (events: CalendarEvent[], today: Date): CalendarSummary => {
    const t = dayjs(today).startOf("day");
    const todays = events.filter((e) => dayjs(effectiveAnchorDate(e, today)).startOf("day").isSame(t));
    if (todays.length) {
        return { kind: "today", count: todays.length };
    }
    const upcoming = events
        .filter((e) => dayjs(effectiveAnchorDate(e, today)).startOf("day").isAfter(t))
        .sort((a, b) => effectiveAnchorDate(a, today).getTime() - effectiveAnchorDate(b, today).getTime());
    if (upcoming.length) {
        const next = upcoming[0];
        return {
            kind: "next",
            nextEvent: next,
            daysUntil: dayjs(effectiveAnchorDate(next, today)).startOf("day").diff(t, "day"),
        };
    }
    return { kind: "empty" };
};
